import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const faqs = [
  {
    q: 'How do I create my first project?',
    a: 'After signing up, go to the Dashboard and click "New Project". Give your project a name and description, then click "Create". You can immediately start adding issues.',
  },
  {
    q: 'How do I invite team members?',
    a: 'Open your company settings and use the "Invite Team Member" button. Enter their email and choose a role (Developer or Tester). They\'ll receive an email invitation to join your workspace.',
  },
  {
    q: 'What is the difference between an Admin, Developer, and Tester?',
    a: 'Admins can manage the company, invite members, create projects, and configure settings. Developers can create issues, update their status, and manage tasks. Testers can log bugs, update review status, and close verified issues.',
  },
  {
    q: 'How do I assign an issue to a developer?',
    a: 'Open any issue and use the "Assigned Developer" dropdown to select a team member. They will be notified automatically and the issue will appear in their assignments.',
  },
  {
    q: 'Can I track progress on a project?',
    a: 'Yes. Each project has a detail view showing all issues by status — Open, In Progress, and Closed. You can filter by priority, severity, and assignee to get a clear picture of progress.',
  },
  {
    q: 'What are Tasks versus Issues?',
    a: 'Issues are bug reports or feature requests tied to a specific project. Tasks are general work items (development, design, meetings, etc.) that can be assigned and tracked independently.',
  },
  {
    q: 'How does the notification system work?',
    a: 'You receive in-app notifications whenever an issue is assigned to you, its status changes, or a review is requested. A digest email is also sent if you accumulate several unread notifications.',
  },
  {
    q: 'Is my data secure?',
    a: 'Yes. Naipeak Issue Tracker is built on Supabase with row-level security (RLS) — you can only see data belonging to your company. All data is transmitted over HTTPS.',
  },
  {
    q: 'Can I use Naipeak Issue Tracker for free?',
    a: 'Yes — the platform is free to use. Sign up, create your workspace, and start tracking bugs with your team today.',
  },
  {
    q: 'How do I reset my password?',
    a: 'On the Login page, click "Forgot password?". Enter your email address and you\'ll receive a reset link. Check your spam folder if the email doesn\'t arrive within a few minutes.',
  },
];

const gettingStarted = [
  {
    step: 1,
    title: 'Create your account',
    desc: 'Sign up at Naipeak Issue Tracker with your work email. No credit card required. You\'ll receive a verification email — click the link to confirm your address.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="24" height="24">
        <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/>
      </svg>
    ),
  },
  {
    step: 2,
    title: 'Set up your company',
    desc: 'After verifying your email, you\'ll be prompted to set up your company workspace. Give it a name — this becomes your team\'s home on the platform.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="24" height="24">
        <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16"/>
      </svg>
    ),
  },
  {
    step: 3,
    title: 'Invite your team',
    desc: 'Go to Settings → Team and invite developers and testers by email. Each invitation includes a unique link and sets their role automatically.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="24" height="24">
        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/>
      </svg>
    ),
  },
  {
    step: 4,
    title: 'Create a project',
    desc: 'From the Dashboard, click "New Project". Fill in the project name and description. You can also set it as public (visible to all team members) or private (invite-only).',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="24" height="24">
        <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/>
      </svg>
    ),
  },
  {
    step: 5,
    title: 'Log your first issue',
    desc: 'Inside your project, click "New Issue". Add a title, description, priority level, and assign it to a developer. Use the rich text editor to add code snippets or screenshots.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="24" height="24">
        <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
      </svg>
    ),
  },
  {
    step: 6,
    title: 'Track & resolve',
    desc: 'Move issues from Open → In Progress → Closed as work progresses. Use comments to collaborate, request reviews, and document decisions. The Dashboard shows progress at a glance.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="24" height="24">
        <polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>
      </svg>
    ),
  },
];

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`faq-item${open ? ' faq-open' : ''}`}>
      <button className="faq-question" onClick={() => setOpen(o => !o)} aria-expanded={open}>
        <span>{q}</span>
        <svg className="faq-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="18" height="18" aria-hidden="true">
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>
      {open && <div className="faq-answer">{a}</div>}
    </div>
  );
}

function ContactSection({ onHelpClick }: { onHelpClick: () => void }) {
  return (
    <section className="help-contact-section">
      <div className="landing-container">
        <div className="help-contact-card">
          <div className="help-contact-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
              <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
            </svg>
          </div>
          <div className="help-contact-content">
            <h3>Still need help?</h3>
            <p>Our support team is ready to assist you. Send us a message and we'll get back to you as soon as possible.</p>
          </div>
          <div className="help-contact-actions">
            <button className="landing-btn-primary" onClick={onHelpClick}>
              Send a message
            </button>
            <a
              href="https://wa.me/254797451014"
              className="landing-btn-secondary"
              target="_blank"
              rel="noopener noreferrer"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16" aria-hidden="true" style={{ marginRight: 6 }}>
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              WhatsApp
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

interface HelpFormData { name: string; email: string; subject: string; message: string; }

function HelpModal({ onClose }: { onClose: () => void }) {
  const [form, setForm]     = useState<HelpFormData>({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [errMsg, setErrMsg] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('sending');
    setErrMsg('');
    try {
      const res = await fetch('/api/help', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send');
      setStatus('sent');
    } catch (err: any) {
      setErrMsg(err.message);
      setStatus('error');
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-panel" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Request Help</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        {status === 'sent' ? (
          <div className="modal-body" style={{ textAlign: 'center', padding: '40px 24px' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
            <h3 style={{ margin: '0 0 8px', color: 'var(--color-text)' }}>Message sent!</h3>
            <p style={{ color: 'var(--color-text-muted)', fontSize: 15 }}>
              We'll get back to you at <strong>{form.email}</strong> as soon as possible.
            </p>
            <button className="btn-primary" style={{ marginTop: 24 }} onClick={onClose}>Close</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Your Name</label>
                <input className="form-input" name="name" value={form.name} onChange={handleChange} required placeholder="John Doe" />
              </div>
              <div className="form-group">
                <label className="form-label">Email Address</label>
                <input className="form-input" name="email" type="email" value={form.email} onChange={handleChange} required placeholder="you@example.com" />
              </div>
              <div className="form-group">
                <label className="form-label">Subject</label>
                <select className="form-select" name="subject" value={form.subject} onChange={handleChange}>
                  <option value="">Select a topic…</option>
                  <option value="Getting started">Getting started</option>
                  <option value="Account & billing">Account &amp; billing</option>
                  <option value="Bug / technical issue">Bug / technical issue</option>
                  <option value="Feature request">Feature request</option>
                  <option value="Team & permissions">Team &amp; permissions</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Message</label>
                <textarea className="form-textarea" name="message" value={form.message} onChange={handleChange} required placeholder="Describe your issue or question in detail…" rows={5} />
              </div>
              {status === 'error' && (
                <div className="error-msg" style={{ marginBottom: 12 }}>{errMsg}</div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={status === 'sending'}>
                {status === 'sending' ? 'Sending…' : 'Send Message'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default function HelpPage() {
  const [helpOpen, setHelpOpen] = useState(false);

  return (
    <div className="help-page">
      {helpOpen && <HelpModal onClose={() => setHelpOpen(false)} />}

      {/* Hero */}
      <section className="help-hero">
        <div className="landing-container">
          <div className="help-hero-back">
            <Link to="/">← Back to home</Link>
          </div>
          <h1 className="help-hero-title">Help Center</h1>
          <p className="help-hero-sub">Everything you need to get started and get the most out of Naipeak Issue Tracker.</p>
        </div>
      </section>

      {/* Getting Started */}
      <section className="landing-section help-gs-section">
        <div className="landing-container">
          <h2 className="landing-section-title">Getting Started</h2>
          <p className="landing-section-sub">Follow these six steps to go from sign-up to shipping faster in minutes.</p>
          <div className="help-gs-grid">
            {gettingStarted.map(s => (
              <div key={s.step} className="help-gs-card">
                <div className="help-gs-icon">{s.icon}</div>
                <div className="help-gs-num">Step {s.step}</div>
                <h3 className="help-gs-title">{s.title}</h3>
                <p className="help-gs-desc">{s.desc}</p>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: 40 }}>
            <Link to="/signup" className="landing-btn-primary">Create free account</Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="landing-section help-faq-section" id="faq">
        <div className="landing-container">
          <h2 className="landing-section-title">Frequently Asked Questions</h2>
          <p className="landing-section-sub">Answers to the most common questions about Naipeak Issue Tracker.</p>
          <div className="faq-list">
            {faqs.map((item, i) => (
              <FaqItem key={i} q={item.q} a={item.a} />
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <ContactSection onHelpClick={() => setHelpOpen(true)} />
    </div>
  );
}
